import { P2GBox, P2GText } from '@parcel2go/core-ui';
import { useEffect, useState } from 'react';
import { ItemActions, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import Loading from '../../../../../components/loading/Loading';
import { EventSections } from '../../../lib/events/EventLogger';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { SmartSendProvider } from '~/lib/consts/providers';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { HasClaims, HasRefund } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';

import Labels from './Labels';
import { EditType } from '../item/EditType';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import SidePanelSection from '~/components/sidepanel-section';
import PrintInStoreQrCodesAutoLoad from './PrintInStoreQrCodesAutoLoad';

interface props {
  item: SmartSendItemApiModel;
  editType: EditType;
}

export default function OrderLabelsPanel(props: props) {
  const { getFullOrder, getOrderActions } = useSmartSendApi();
  const [actions, setActions] = useState<ItemActions>(null);
  const [item, setItem] = useState<SmartSendItemApiModel>({ ...props.item });
  const [loading, setLoading] = useState(true);
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const hasClaim = HasClaims(item);
  const hasRefund = HasRefund(item);
  const inTransit = !!item.TrackingSummary?.DroppedOff || !!item.TrackingSummary?.Collected;

  useEffect(() => {
    track({
      type: 'edit_item_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.BookedItems, 'order-labels-panel']),
        action: props.editType
      }
    });
  }, []);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);

      // Try and get the full object from the API.
      const o = await getFullOrder(SmartSendProvider, SmartSendProvider, props.item.Id);
      if (o.success) {
        setItem(o.result.Order);
      }

      // Now get the actions
      const a = await getOrderActions(SmartSendProvider, SmartSendProvider, item.Id);
      if (a.success) {
        setActions(a.result);
      }

      setLoading(false);
    };
    getData();
  }, [props.item?.Id]);

  if (loading || !item || !actions || !actions.Links) {
    return <Loading />;
  }

  if (hasClaim || hasRefund) {
    return (
      <P2GText variant="body2">
        {t('LABELS_NOT_AVAILABLE', {
          defaultValue: 'Labels are not available for items with claims or refunds.'
        })}
      </P2GText>
    );
  }

  if (inTransit) {
    return (
      <P2GText variant="body2">
        {t('LABELS_NOT_AVAILABLE_IN_TRANSIT', {
          defaultValue: 'Labels are not available for items that are in transit.'
        })}
      </P2GText>
    );
  }

  return (
    <>
      <PrintInStoreQrCodesAutoLoad
        item={props.item}
        actions={actions.Links}
        wrapper={(x) => (
          <SidePanelSection title={t('YOUR_PIS_CODE_SUBTITLE', { defaultValue: 'Your Print In Store Code' })}>
            <P2GBox py={1}>{x}</P2GBox>
          </SidePanelSection>
        )}
      />
      <Labels item={item} actions={actions} />
    </>
  );
}
